import { crmSupabase } from "./crm-client";
import type {
  Action,
  DigestRow,
  EstrellaScore,
  NonEstrellaScore,
  TodayPayload,
  Urgency,
} from "./crm-types";

// ----------------------------------------------------------------------------
// Mock fallback — used when VITE_CRM_SUPABASE_* env vars are not set.
// Mirrors the seed v0 + refresh v1 state so the UI is meaningful pre-DB-hookup.
// ----------------------------------------------------------------------------

const mockToday = (): TodayPayload => {
  const daysAgo = (n: number) =>
    new Date(Date.now() - n * 86400_000).toISOString().slice(0, 10);

  const mkAction = (
    id: string,
    name: string,
    cat: string,
    color: string,
    what: string,
    daysLate: number,
    isEstrella = true,
  ): Action => ({
    id,
    what,
    priority: daysLate > 5 ? 1 : daysLate > 0 ? 2 : 3,
    ball_in_court: "me",
    due_date: daysAgo(Math.max(0, daysLate)),
    days_late: daysLate,
    lead_id: id,
    full_name: name,
    is_estrella: isEstrella,
    category_name: cat,
    category_color: color,
    last_contact_at: daysAgo(daysLate + 2),
    urgency:
      daysLate > 3 ? "late_bad" : daysLate > 0 ? "late_mid" : "due_today",
    primary_email: null,
    primary_phone: null,
  });

  const late: Action[] = [
    mkAction("a1", "Quentin Nickmans", "PME (Hexa)", "#FFFC7D",
      "Envoyer recap après visite Hexa", 12),
    mkAction("a2", "Renaud Pestre", "PME (Pretto)", "#FFFC7D",
      "Recontacter Pretto (dernier échange 27/03)", 8),
    mkAction("a3", "Baptiste Benezet", "PME", "#FFFC7D",
      "Envoyer mail draft du 19/04 ou recomposer", 7),
    mkAction("a4", "Hadrien Raise", "Org. événements", "#4F7FB5",
      "Premier contact (intro Henri Delahaye)", 6),
    mkAction("a5", "Guillaume Tissier", "Org. événements", "#4F7FB5",
      "Reprendre discussion post-INCYBER", 6),
    mkAction("a6", "Henri Delahaye", "Org. événements", "#4F7FB5",
      "Demander emails Adrien + Shane", 6),
  ];

  const due_today: Action[] = [
    mkAction("a7", "Xavier Dalloz", "Autres", "#B5B0A6",
      "Préparer meeting Xavier 14/05 12pm EST", 0),
    mkAction("a8", "Marie Castel", "PME (Pretto)", "#FFFC7D",
      "Recap post-meeting Pretto Galaxie", 0),
    mkAction("a9", "Camille Goni", "PME (Hexa)", "#FFFC7D",
      "Suivre réponse Hexa relance 11/05", 0),
    mkAction("a10", "Thibaud Elzière", "PME (Hexa)", "#FFFC7D",
      "Suivre réponse Hexa relance 11/05", 0),
    mkAction("a11", "Amaury Pulchre", "PME (Hexa)", "#FFFC7D",
      "Suivre réponse Hexa relance 11/05", 0),
    mkAction("a12", "Benjamin Bitton", "PME (2CFinance)", "#FFFC7D",
      "Suivre réponse 2CFinance (mail 11/05)", 0),
    mkAction("a13", "Yves Benchimol", "PME (Weward)", "#FFFC7D",
      "Suivre réponse Weward (mail 11/05)", 0),
    mkAction("a14", "Sian Kelly Guy", "Org. événements", "#4F7FB5",
      "Reproposer slot après decline 13/05", 0),
    mkAction("a15", "Serge Vatine", "PME (BOLD)", "#FFFC7D",
      "Reprendre suite meeting summary 5/05", 0),
    mkAction("a16", "Fiona McKnight", "PME (BOLD)", "#FFFC7D",
      "Préparer meeting GNIP Fiona 19/05 7am", 0),
    mkAction("a17", "Philippe Mineur", "PME (Pretto)", "#FFFC7D",
      "Suivre threads Pretto RH + Finance", 0),
  ];

  const scores_14d: DigestRow[] = [
    [13, 70], [12, 75], [11, 70], [10, 73], [9, 75], [8, 73], [7, 75],
    [6, 78], [5, 72], [4, 70], [3, 68], [2, 75], [1, 82], [0, 70],
  ].map(([d, s]) => ({
    digest_date: daysAgo(d),
    estrella_score: s,
    non_estrella_score: Math.min(95, s + 13),
    mails_sent_today: 0,
  }));

  const mails_21d: DigestRow[] = [
    [20, 0], [19, 0], [18, 0], [17, 2], [16, 8], [15, 6], [14, 5],
    [13, 6], [12, 7], [11, 12], [10, 0], [9, 4], [8, 3], [7, 2],
    [6, 6], [5, 7], [4, 5], [3, 3], [2, 12], [1, 2], [0, 0],
  ].map(([d, m]) => ({
    digest_date: daysAgo(d),
    estrella_score: 0,
    non_estrella_score: 0,
    mails_sent_today: m,
  }));

  return {
    scores: {
      estrella: { estrella_total: 20, estrella_overdue: 6, estrella_score: 70 },
      non_estrella: {
        non_estrella_total: 41,
        recently_touched: 36,
        non_estrella_score: 88,
      },
    },
    actions: { late, due_today },
    histograms: { scores_14d, mails_21d },
    source: "mock",
  };
};

// ----------------------------------------------------------------------------
// Real fetch from Supabase (PostgREST). Falls back to mock on any failure.
// ----------------------------------------------------------------------------

export async function getTodayDashboard(): Promise<TodayPayload> {
  if (!crmSupabase) return mockToday();

  try {
    const [
      { data: estrella, error: e1 },
      { data: nonEstrella, error: e2 },
      { data: digests14, error: e3 },
      { data: digests21, error: e4 },
    ] = await Promise.all([
      crmSupabase.from("v_estrella_score_today").select("*").single(),
      crmSupabase.from("v_non_estrella_score_today").select("*").single(),
      crmSupabase
        .from("daily_digest")
        .select("digest_date,estrella_score,non_estrella_score,mails_sent_today")
        .gte(
          "digest_date",
          new Date(Date.now() - 14 * 86400_000).toISOString().slice(0, 10),
        )
        .order("digest_date", { ascending: true }),
      crmSupabase
        .from("daily_digest")
        .select("digest_date,mails_sent_today,estrella_score,non_estrella_score")
        .gte(
          "digest_date",
          new Date(Date.now() - 21 * 86400_000).toISOString().slice(0, 10),
        )
        .order("digest_date", { ascending: true }),
    ]);

    if (e1 || e2 || e3 || e4) throw new Error("supabase fetch error");

    // Actions: query the next_action + lead + category + identities in one go.
    const { data: actionsRaw, error: e5 } = await crmSupabase
      .from("next_action")
      .select(
        "id,what,priority,ball_in_court,due_date,status,lead:lead_id(id,full_name,is_estrella,last_contact_at,category:category_id(name,color_hex),identities:lead_identity(kind,value,is_primary))",
      )
      .in("status", ["open", "late"])
      .order("priority", { ascending: true })
      .limit(30);

    if (e5 || !actionsRaw) throw new Error("supabase actions error");

    const flatActions: Action[] = (actionsRaw as any[]).map((r) => {
      const due = new Date(r.due_date).getTime();
      const today = Date.now();
      const daysLate = Math.max(
        0,
        Math.floor((today - due) / 86400_000),
      );
      const urgency: Urgency =
        daysLate > 3 ? "late_bad" : daysLate > 0 ? "late_mid" : "due_today";
      const identities: { kind: string; value: string; is_primary: boolean }[] =
        r.lead?.identities ?? [];
      const primaryEmail =
        identities.find((i) => i.kind === "email" && i.is_primary)?.value ?? null;
      const primaryPhone =
        identities.find((i) => i.kind === "phone")?.value ?? null;
      return {
        id: r.id,
        what: r.what,
        priority: r.priority,
        ball_in_court: r.ball_in_court,
        due_date: r.due_date,
        days_late: daysLate,
        lead_id: r.lead?.id ?? "",
        full_name: r.lead?.full_name ?? "—",
        is_estrella: r.lead?.is_estrella ?? false,
        category_name: r.lead?.category?.name ?? "—",
        category_color: r.lead?.category?.color_hex ?? "#B5B0A6",
        last_contact_at: r.lead?.last_contact_at ?? null,
        urgency,
        primary_email: primaryEmail,
        primary_phone: primaryPhone,
      };
    });

    return {
      scores: {
        estrella: estrella as EstrellaScore,
        non_estrella: nonEstrella as NonEstrellaScore,
      },
      actions: {
        late: flatActions.filter((a) => a.urgency !== "due_today"),
        due_today: flatActions.filter((a) => a.urgency === "due_today"),
      },
      histograms: {
        scores_14d: (digests14 ?? []) as DigestRow[],
        mails_21d: (digests21 ?? []) as DigestRow[],
      },
      source: "supabase",
    };
  } catch {
    return mockToday();
  }
}
